import { useContext, useEffect, useState, type ChangeEvent } from 'react'
import { Button, Hidden, Input, InputMask } from 'applaus-ui-kit'
import { useFormik } from 'formik'
import TemplateImage from '../../assets/templateImage.png'
import * as S from './CustomerSignup.styles'
import UnauthenticatedTemplate from '../../components/unauthenticatedTemplate/Unauthenticated'
import { history } from '../../routes/services/history'
import { createCustomerService } from '../../api/services/customer/createCustomerService'
import { customerSchema } from './validation/customerSchema'
import { AxiosError } from 'axios'
import { ButtonBack } from '../../components/buttonBack/ButtonBack'
import { AuthContext } from '../../contexts/AuthContext'
import Logo from '../../assets/applaus-logo-white.png'
import ReactSelect from 'react-select'
import jsonStates from '../../utils/estados-cidades.json'
import { useWindowSize } from '../../hooks/useWindowSize'
import { getInterestsService, Interest } from '../../api/services/interests/getInterestsService'
import { getCepService } from '../../api/services/getCepService'
import { IconBack, Title} from '../../components/buttonBack/ButtonBack.styles'

const genderOptions = [
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'FEMININO', label: 'Feminino' },
  { value: 'OUTRO', label: 'Outro' },
  { value: 'PREFIRO_NAO_INFORMAR', label: 'Prefiro não informar' }
]

const CustomerSignup = () => {
  const { setLoading, setModalState } = useContext(AuthContext)
  const { isMobile } = useWindowSize()
  const [states] = useState<{ value: string; label: string }[]>(
    jsonStates.estados.map((state) => ({
      value: state.sigla,
      label: state.nome
    }))
  )
  const [cities, setCities] = useState<{ value: string; label: string }[]>([])
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedState, setSelectedState] = useState('')
  const [profileImage, setProfileImage] = useState<File | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string>('')
  const [selectedInterests, setSelectedInterests] = useState<string[]>([])
  const [interests, setInterests] = useState<Interest[]>([])

  useEffect(() => {
    if (selectedState !== '') {
      setCities(
        jsonStates.estados
          .filter((state) => state.sigla === selectedState)[0]
          .cidades.map((city) => ({
            value: city,
            label: city
          }))
      )
    }
  }, [selectedState])

  useEffect(() => {
    const fetchInterests = async () => {
      try {
        const data = await getInterestsService()
        setInterests(data)
      } catch (error) {
        console.error('Erro ao buscar interesses:', error)
      }
    }

    fetchInterests()
  }, [])

  const resetAlertModal = () => {
    setModalState({
      message: '',
      open: false,
      variant: 'error'
    })
  }

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setProfileImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleInterestToggle = (interestId: string) => {
    setSelectedInterests(prev =>
      prev.includes(interestId)
        ? prev.filter(i => i !== interestId)
        : [...prev, interestId]
    )
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      lastName: '',
      cpf: '',
      email: '',
      confirmPassword: '',
      password: '',
      phone: '',
      city: '',
      state: '',
      district: '',
      street: '',
      number: '',
      zipCode: '',
      gender: '',
      birthDate: '',
      complement: '',
    },
    validationSchema: customerSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true)
        await createCustomerService({
          firstName: values.name,
          lastName: values.lastName,
          document: values.cpf,
          email: values.email,
          password: values.password,
          phone: values.phone,
          image: profileImage || undefined,
          categories: selectedInterests,
          gender: values.gender,
          birthDate: values.birthDate,
          address: {
            city: values.city,
            state: values.state,
            country: 'BR',
            district: values.district,
            number: Number(values.number),
            street: values.street,
            zipCode: values.zipCode,
            complement: values.complement,
          }
        })
        formik.resetForm()
        setLoading(false)
        setModalState({
          message: 'Parabéns! Cadastro feito com sucesso.',
          open: true,
          variant: 'success',
          click: () => {
            setModalState({
              message: '',
              open: false,
              variant: 'error'
            })
            history.push('/')
          }
        })
      } catch (err: any | AxiosError) {
        setLoading(false)
        if (err.response) {
          if (err.response.status === 409) {
            if (err.response.data.message === 'email already exists') {
              setModalState({
                buttonText: 'Fechar',
                click: () => resetAlertModal(),
                message: 'E-mail inserido já cadastrado',
                open: true,
                variant: 'error'
              })
            }

            if (err.response.data.message === 'document already exists') {
              setModalState({
                buttonText: 'Fechar',
                click: () => resetAlertModal(),
                message: 'CPF inserido já cadastrado',
                open: true,
                variant: 'error'
              })
            }
          } else {
            setModalState({
              buttonText: 'Fechar',
              click: () => resetAlertModal(),
              message: 'Ocorreu Algum erro, tente novamente mais tarde',
              open: true,
              variant: 'error'
            })
          }
        } else {
          setModalState({
            buttonText: 'Fechar',
            click: () => resetAlertModal(),
            message: 'Ocorreu Algum erro, tente novamente mais tarde',
            open: true,
            variant: 'error'
          })
        }
      }
    }
  })

  const handleCep = async (e: string) => {
    if (e.replace('-', '').replace(' ', '').length > 7) {
      const response: any = await getCepService(e)
      if (response.erro === undefined) {
        setSelectedState(response.uf.toUpperCase())
        formik.setFieldValue('state', response.uf.toUpperCase())
        formik.setFieldValue('city', response.localidade)
        formik.setFieldValue('street', response.logradouro ?? '')
        formik.setFieldValue('district', response.bairro ?? '')
      }
    }
  }

  const handleNextStep = async () => {
    if (currentStep === 1) {
      const firstStepFields = [
        'name',
        'lastName',
        'cpf',
        'email',
        'phone',
        'zipCode',
        'state',
        'city',
        'street',
        'district',
        'number',
        'complement',
        'gender',
        'birthDate'
      ]

      // Tocar todos os campos para ativar a validação
      firstStepFields.forEach(field => {
        formik.setFieldTouched(field, true, false)
      })

      // Validar campos
      const errors = await formik.validateForm()
      const hasErrors = firstStepFields.some(field => Boolean(errors[field as keyof typeof formik.values]))

      if (!hasErrors) {
        setCurrentStep(2)
      }
    } else if (currentStep === 2) {
      // Validar campos de senha
      await formik.setFieldTouched('password', true, false)
      await formik.setFieldTouched('confirmPassword', true, false)
      const errors = await formik.validateForm()

      if (!errors.password && !errors.confirmPassword) {
        setCurrentStep(3)
      }
    }
  }

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1)
    } else if (currentStep === 3) {
      setCurrentStep(2)
    } else {
      history.goBack()
    }
  }

  return (
    <S.Container>
      <UnauthenticatedTemplate image={TemplateImage}>
        <S.Wrapper>
          <Hidden mobile>
            <S.Logo src={Logo} />
          </Hidden>
          <S.ContentForm>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "left" }} onClick={handleBack}>
              <IconBack />
              <Title colorText={'white'}>Voltar</Title>
            </div>
            <S.TextRegister>Cadastre-se</S.TextRegister>
            <S.TextObrigatory>*Obrigatório</S.TextObrigatory>
            <S.Form onSubmit={(e) => {
              // Prevenir submissão do form se não estiver no último passo
              if (currentStep !== 3) {
                e.preventDefault()
                return
              }
              formik.handleSubmit(e)
            }}>
              {currentStep === 1 ? (
                <>
                  <Input
                    label="*Nome"
                    name="name"
                    placeholder="Nome"
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    texterror={formik.errors.name}
                    onChange={formik.handleChange}
                    value={formik.values.name}
                    fullWidth
                  />
                  <Input
                    label="*Sobrenome"
                    name="lastName"
                    placeholder="Sobrenome"
                    error={
                      formik.touched.lastName && Boolean(formik.errors.lastName)
                    }
                    texterror={formik.errors.lastName}
                    onChange={formik.handleChange}
                    value={formik.values.lastName}
                    fullWidth
                  />
                  <Input
                    label="*Email"
                    name="email"
                    placeholder="Email"
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    texterror={formik.errors.email}
                    onChange={formik.handleChange}
                    value={formik.values.email}
                    fullWidth
                  />

                  <InputMask
                    label="*Telefone"
                    mask="(99) 99999-9999"
                    name="phone"
                    placeholder="Digite seu telefone"
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    texterror={formik.errors.phone}
                    onChange={formik.handleChange}
                    value={formik.values.phone}
                    fullWidth
                  />
                  <InputMask
                    mask="999.999.999-99"
                    label="*CPF"
                    name="cpf"
                    placeholder="CPF"
                    error={formik.touched.cpf && Boolean(formik.errors.cpf)}
                    texterror={formik.errors.cpf}
                    onChange={formik.handleChange}
                    value={formik.values.cpf}
                    fullWidth
                  />

                  <InputMask
                    mask="99999-999"
                    name="zipCode"
                    label="*CEP"
                    placeholder="Digite seu CEP"
                    value={formik.values.zipCode}
                    error={
                      formik.touched.zipCode && Boolean(formik.errors.zipCode)
                    }
                    texterror={formik.errors.zipCode}
                    onChange={(e) => {
                      formik.handleChange(e)
                      handleCep(e.target.value)
                    }}
                    fullWidth
                  />

                  <div style={{ marginBottom: '16px' }}>
                    <S.Label>*Estado</S.Label>
                    <ReactSelect
                      options={states}
                      onChange={(e) => {
                        setSelectedState(e!.value)
                        formik.setFieldValue('state', e!.value)
                        formik.setFieldValue('city', '')
                      }}
                      value={states.filter(
                        (obj) => formik.values.state === obj.value
                      )}
                      noOptionsMessage={() => 'Estado não encontrado'}
                      placeholder="Estado"
                      styles={S.customStyles}
                    />
                    <S.Error>
                      {formik.touched.state && Boolean(formik.errors.state)
                        ? formik.errors.state
                        : null}
                    </S.Error>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <S.Label>*Cidade</S.Label>
                    <ReactSelect
                      options={cities}
                      onChange={(e) => formik.setFieldValue('city', e!.value)}
                      value={cities.filter(
                        (obj) => formik.values.city === obj.value
                      )}
                      noOptionsMessage={() =>
                        selectedState
                          ? 'Cidade não encontrada'
                          : 'Selecione o estado'
                      }
                      placeholder="Cidade"
                      styles={S.customStyles}
                    />
                    <S.Error>
                      {formik.touched.city && Boolean(formik.errors.city)
                        ? formik.errors.city
                        : null}
                    </S.Error>
                  </div>
                  <div style={{marginBottom: '16px'}}>
                    <Input
                      name="street"
                      label="*Endereço"
                      placeholder="Digite seu endereço"
                      value={formik.values.street}
                      error={formik.touched.street && Boolean(formik.errors.street)}
                      texterror={formik.errors.street}
                      onChange={formik.handleChange}
                      fullWidth
                    />
                  </div>
                  <div style={{marginBottom: '16px'}}>
                    <Input
                        name="district"
                        label="*Bairro"
                        placeholder="Digite seu bairro"
                        value={formik.values.district}
                        error={
                          formik.touched.district && Boolean(formik.errors.district)
                        }
                        texterror={formik.errors.district}
                        onChange={formik.handleChange}
                        fullWidth
                      />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Input
                      name="number"
                      label="*Número"
                      type="number"
                      placeholder="Digite seu número"
                      value={formik.values.number}
                      error={formik.touched.number && Boolean(formik.errors.number)}
                      texterror={formik.errors.number}
                      onChange={formik.handleChange}
                      fullWidth
                    />
                  </div>

                  <div style={{ marginBottom: '16px'}}>
                    <Input
                      name="complement"
                      label="Complemento"
                      placeholder="Digite o complemento"
                      value={formik.values.complement}
                      error={formik.touched.complement && Boolean(formik.errors.complement)}
                      texterror={formik.errors.complement}
                      onChange={formik.handleChange}
                      fullWidth
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <S.Label>*Gênero</S.Label>
                    <ReactSelect
                      options={genderOptions}
                      onChange={(e) => formik.setFieldValue('gender', e!.value)}
                      value={genderOptions.find(
                        (obj) => formik.values.gender === obj.value
                      )}
                      noOptionsMessage={() => 'Opção não encontrada'}
                      placeholder="Selecione seu gênero"
                      styles={S.customStyles}
                    />
                    <S.Error>
                      {formik.touched.gender && Boolean(formik.errors.gender)
                        ? formik.errors.gender
                        : null}
                    </S.Error>
                  </div>

                  <InputMask
                    mask="99/99/9999"
                    label="*Data de Nascimento"
                    name="birthDate"
                    placeholder="DD/MM/AAAA"
                    error={formik.touched.birthDate && Boolean(formik.errors.birthDate)}
                    texterror={formik.errors.birthDate}
                    onChange={formik.handleChange}
                    value={formik.values.birthDate}
                    fullWidth
                  />

                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    onClick={handleNextStep}
                    type="button"
                    fullWidth>
                    Avançar
                  </Button>
                </>
              ) : currentStep === 2 ? (
                <>
                  <S.ProfileSection>
                    <S.ProfileImageContainer>
                      {profileImagePreview ? (
                        <S.ProfileImage src={profileImagePreview} alt="Foto de perfil" />
                      ) : (
                        <S.ProfilePlaceholder>
                          <S.UserIcon />
                        </S.ProfilePlaceholder>
                      )}
                      <S.ImageInput
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        id="profile-image"
                      />
                      <S.AddImageButton htmlFor="profile-image">
                        <S.PlusIcon />
                      </S.AddImageButton>
                    </S.ProfileImageContainer>
                    <S.Label>Foto de perfil</S.Label>
                  </S.ProfileSection>

                  <Input
                    label="*Senha"
                    name="password"
                    placeholder="Senha"
                    type="password"
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    texterror={formik.errors.password}
                    onChange={formik.handleChange}
                    value={formik.values.password}
                    fullWidth
                  />
                  <Input
                    label="*Confirmar Senha"
                    name="confirmPassword"
                    placeholder="Confirmar Senha"
                    type="password"
                    error={
                      formik.touched.confirmPassword &&
                      Boolean(formik.errors.confirmPassword)
                    }
                    texterror={formik.errors.confirmPassword}
                    onChange={formik.handleChange}
                    value={formik.values.confirmPassword}
                    fullWidth
                  />
                  <S.RulesPassword>
                    <S.RulePassword>* Pelo menos 8 caracteres</S.RulePassword>
                    <S.RulePassword>* No mínimo 1 letra e 1 número</S.RulePassword>
                  </S.RulesPassword>

                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    onClick={handleNextStep}
                    type="button"
                    fullWidth>
                    Avançar
                  </Button>
                </>
              ) : (
                <>
                  <S.InterestsSection>
                    <div>
                      <S.InterestsTitle>Boas-vindas!</S.InterestsTitle>
                      <S.InterestsSubtitle>
                        Selecione as categorias mais alinhadas com seus interesses:
                      </S.InterestsSubtitle>
                    </div>

                    <S.InterestsGrid>
                      {interests.map((interest) => (
                        <S.InterestButton
                          key={interest.id}
                          selected={selectedInterests.includes(interest.id)}
                          onClick={() => handleInterestToggle(interest.id)}
                          type="button"
                        >
                          {interest.name}
                        </S.InterestButton>
                      ))}
                    </S.InterestsGrid>
                  </S.InterestsSection>

                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    type="submit"
                    fullWidth>
                    Cadastrar
                  </Button>
                </>
              )}
            </S.Form>
          </S.ContentForm>
        </S.Wrapper>
      </UnauthenticatedTemplate>
    </S.Container>
  )
}
export { CustomerSignup }
